import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { Loader } from "components/Loader";
import { designType } from "services/constants/designTokens";
import { FilterSelect, StatutoryPage, StatutoryTabs } from "pages/PayRoll/Shared/statutoryUi";
import { fetchPtSiteOptions } from "pages/PayRoll/Statutory/PT/ptEmployeesApi";
import { fetchDepartmentOptions, fetchPtOverview, fetchPtReportMonths } from "./ptReportsApi";
import { PtOverviewTab } from "./PtOverviewTab";
import { PtRegisterTab } from "./PtRegisterTab";
import { PtPaymentsTab } from "./PtPaymentsTab";
import { PtValidationTab } from "./PtValidationTab";

// Payroll > Reports > Statutory Reports > Professional Tax (screens PT3–
// PT6a), on the PT Reports API (ptReportsApi.js). Built only from
// finalised payroll: the Payroll Month list offers finalised months only.
// Site and Department filters apply to every tab (Department does not
// apply to Payments or Validation). The company pays PT to the state or
// local body and records the payment here.

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "register", label: "PT Register" },
  { value: "payments", label: "Payments" },
  { value: "validation", label: "Validation" },
];
const ALL = "all";

export const PtStatutoryReports = ({ onBack, initialTab }) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh state per company.
  return <PtReportsScreen key={`${domain}:${topbarCompanyId}`} domain={domain} companyId={topbarCompanyId} onBack={onBack} initialTab={initialTab} />;
};

const PtReportsScreen = ({ domain, companyId, onBack, initialTab }) => {
  const [tab, setTab] = useState(() => (TABS.some((item) => item.value === initialTab) ? initialTab : "overview"));
  const [months, setMonths] = useState({ loading: true, error: "", list: [] });
  const [monthsKey, setMonthsKey] = useState(0);
  const [month, setMonth] = useState("");
  const [siteOptions, setSiteOptions] = useState([]);
  const [siteFilter, setSiteFilter] = useState(ALL);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [department, setDepartment] = useState(ALL);
  const [overview, setOverview] = useState({ loading: true, error: "", data: null });
  const [overviewKey, setOverviewKey] = useState(0);
  // Where a card or report row sends the user: Register status, or a
  // payment period to open.
  const [registerStatus, setRegisterStatus] = useState(ALL);
  const [openPeriod, setOpenPeriod] = useState(null);

  // Finalised months (latest first), sites and departments.
  useEffect(() => {
    const controller = new AbortController();
    setMonths((current) => ({ ...current, loading: true, error: "" }));
    fetchPtReportMonths({ domain, companyId, signal: controller.signal })
      .then((list) => {
        setMonths({ loading: false, error: "", list });
        setMonth((current) => (list.some((item) => item.value === current) ? current : list[0]?.value || ""));
      })
      .catch((error) => {
        if (!controller.signal.aborted) setMonths({ loading: false, error: error.message || "Could not load the payroll months.", list: [] });
      });
    return () => controller.abort();
  }, [domain, companyId, monthsKey]);

  useEffect(() => {
    const controller = new AbortController();
    fetchPtSiteOptions({ domain, companyId, signal: controller.signal })
      .then(setSiteOptions)
      .catch(() => {});
    fetchDepartmentOptions({ domain }).then(setDepartmentOptions);
    return () => controller.abort();
  }, [domain, companyId]);

  const siteId = siteFilter === ALL ? undefined : siteFilter;
  const departmentId = department === ALL ? undefined : department;

  // The overview also gives the header's payroll status.
  useEffect(() => {
    if (!month) return undefined;
    const controller = new AbortController();
    setOverview((current) => ({ ...current, loading: true, error: "" }));
    fetchPtOverview({ domain, companyId, month, siteId, departmentId, signal: controller.signal })
      .then((data) => setOverview({ loading: false, error: "", data }))
      .catch((error) => {
        if (!controller.signal.aborted) setOverview((current) => ({ ...current, loading: false, error: error.message || "Could not load the PT overview." }));
      });
    return () => controller.abort();
  }, [domain, companyId, month, siteId, departmentId, overviewKey]);

  const goTo = (next, options = {}) => {
    if (next === "register") setRegisterStatus(options.status || ALL);
    if (next === "payments") setOpenPeriod(options.periodId ? { id: options.periodId, kind: options.kind || "view" } : null);
    setTab(next);
  };
  const changeTab = (next) => goTo(next);
  const refreshOverview = () => setOverviewKey((key) => key + 1);

  const monthInfo = months.list.find((item) => item.value === month);
  const payroll = overview.data?.payroll;
  const aside = payroll?.status ? (
    <Mui.Box sx={{ textAlign: { md: "right" } }}>
      <Mui.Stack direction="row" spacing={1} alignItems="center" justifyContent={{ md: "flex-end" }}>
        <Mui.Typography sx={designType.helperText}>Payroll status</Mui.Typography>
        <RequestStatusChip status={payroll.status} colorCode="#12B76A" />
      </Mui.Stack>
      {payroll.label ? <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>{payroll.label}</Mui.Typography> : null}
    </Mui.Box>
  ) : null;

  const filters = { domain, companyId, month, monthLabel: monthInfo?.label || month, siteId, departmentId };

  let body;
  if (months.loading && !months.list.length) {
    body = <Loader info="Loading..." minHeight="40vh" />;
  } else if (months.error) {
    body = (
      <RequestInfoBanner
        tone="danger"
        title="Couldn't load the payroll months."
        body={months.error}
        action={<SecondaryButton onClick={() => setMonthsKey((key) => key + 1)} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
      />
    );
  } else {
    // Overview and Register need a finalised month; Payments and Validation
    // work without one (Validation then checks the current month).
    const noMonth = <RequestInfoBanner title="No finalised payroll yet." body="The overview and the PT register are built from finalised payroll. They show here once the first payroll month is finalised. Payments and Validation are available now." />;
    body = (
      <>
        <StatutoryTabs value={tab} onChange={changeTab} tabs={TABS} />
        {tab === "overview" ? month ? <PtOverviewTab {...filters} overview={overview} onRetry={refreshOverview} goTo={goTo} /> : noMonth : null}
        {tab === "register" ? month ? <PtRegisterTab key={`${month}:${registerStatus}`} {...filters} initialStatus={registerStatus} /> : noMonth : null}
        {tab === "payments" ? <PtPaymentsTab {...filters} openPeriod={openPeriod} onChanged={refreshOverview} /> : null}
        {tab === "validation" ? <PtValidationTab {...filters} goTo={goTo} onChanged={refreshOverview} /> : null}
      </>
    );
  }

  return (
    <StatutoryPage
      breadcrumb={["Payroll", "Reports", "Statutory Reports", "Professional Tax"]}
      title="PT Reports"
      subtitle="Built from finalised payroll. Cauver works out PT for each site — your organisation pays it to the state or local body."
      aside={aside}
      onBack={onBack}
    >
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <FilterSelect
          label="Payroll Month"
          value={month}
          onChange={setMonth}
          options={months.list.length ? months.list : [{ value: "", label: months.loading ? "Loading…" : "No finalised months" }]}
        />
        <FilterSelect label="Site" value={siteFilter} onChange={setSiteFilter} options={[{ value: ALL, label: "All sites" }, ...siteOptions]} />
        <FilterSelect label="Department" value={department} onChange={setDepartment} options={[{ value: ALL, label: "All departments" }, ...departmentOptions]} />
      </Mui.Stack>
      {body}
    </StatutoryPage>
  );
};
